import { useParams } from "react-router";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useEffect, useState, useCallback } from "react";
import { mapContentSettingsInput } from "DDD/action-objects/ProposalSend";
import usePreviewProposal from "hooks/proposals/usePreviewProposal";
import useCustomGetProposal from "hooks/proposals/useCustomGetProposal";
import { PreviewProposalMutationVariables, ProposalLayout } from "generated/graphql";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import AbsoluteCenterProgressLoader from "components/AbsoluteCenterProgressLoader/AbsoluteCenterProgressLoader";
import { getMissingJobSizeMessage } from "modules/proposals/validators";

export default function ProposalPreviewPage() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const { data: proposal, loading: proposalLoading } = useCustomGetProposal({ id: proposalId });
  const [previewProposal, { isSuccess }] = usePreviewProposal();
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<PreviewProposalMutationVariables | null>(null);

  const handlePreviewProposal = useCallback(
    async (variables: PreviewProposalMutationVariables) => {
      const result = await previewProposal({
        variables,
      });
      if (isSuccess(result.data) && result.data?.previewProposal) {
        setError(null);
        window.location.href = result.data.previewProposal;
        return true;
      }
      // @ts-ignore
      if (result?.message?.includes("Please input a job size.")) {
        // @ts-ignore
        setError(result.message);
        setOpen(true);
      } else {
        // @ts-ignore
        if (result?.message) {
          // @ts-ignore
          setError(result.message);
        } else {
          setError("Failed to generate proposal preview.");
        }
      }
      return false;
    },
    [previewProposal, isSuccess]
  );

  const acceptFn = useCallback(async () => {
    if (!previewData) return false;
    return await handlePreviewProposal({ ...previewData, forceSend: true });
  }, [handlePreviewProposal, previewData]);

  const cancelFn = useCallback(async () => {
    window.close();
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  useEffect(() => {
    if (proposal && proposalId) {
      const searchParams = new URLSearchParams(window.location.search);
      const urlLayout = searchParams.get("layout");
      const urlRemoveUnitPrice = searchParams.get("removeUnitPriceAndQuantity");
      const urlRemoveIndividualPricing = searchParams.get("removeIndividualLineItemPricing");
      const urlAttachments = searchParams.get("attachments");
      const urlContentSettings = searchParams.get("contentSettings");
      const urlAcceptanceOverride = searchParams.get("acceptanceOverride");
      const urlEmailSubject = searchParams.get("emailSubject");
      const urlEmailBody = searchParams.get("emailBody");
      const urlClientEmail = searchParams.get("clientEmail");
      const urlForceSend = searchParams.get("forceSend");

      const variables: PreviewProposalMutationVariables = {
        proposalId,
        layout: (urlLayout as ProposalLayout) || proposal.layout || ProposalLayout.SIMPLE,
        removeUnitPriceAndQuantity:
          urlRemoveUnitPrice === "true" || proposal.removeUnitPriceAndQuantity || false,
        removeIndividualLineItemPricing:
          urlRemoveIndividualPricing === "true" ||
          proposal.removeIndividualLineItemPricing ||
          false,
        attachments: urlAttachments
          ? JSON.parse(urlAttachments)
          : proposal.attachments?.map((at) => at.id) ?? [],
        contentSettings: urlContentSettings
          ? JSON.parse(urlContentSettings)
          : mapContentSettingsInput(
              proposal.contentSettings,
              proposal.contentSettings?.filter((item) => item.isEnabled).map((item) => item.id) ??
                []
            ),
        acceptanceOverride: urlAcceptanceOverride || proposal.acceptanceOverride,
        emailSubjectOverride: urlEmailSubject || proposal.emailSubjectOverride,
        emailBodyOverride: urlEmailBody || proposal.emailBodyOverride,
        clientEmailOverride: urlClientEmail || proposal.clientEmailOverride,
        forceSend: urlForceSend === "true",
      };
      setPreviewData(variables);
      const missingJobSizeMessage = getMissingJobSizeMessage(proposal);
      if (missingJobSizeMessage && !variables.forceSend) {
        setError(missingJobSizeMessage);
        setOpen(true);
      } else {
        handlePreviewProposal(variables);
      }
    }
  }, [proposal, proposalId, handlePreviewProposal]);

  if (proposalLoading) {
    return <AbsoluteCenterProgressLoader />;
  }

  if (!proposalId) {
    return (
      <MDBox pt={6} pb={3} textAlign="center">
        <MDTypography variant="h4">Proposal not found</MDTypography>
      </MDBox>
    );
  }

  return (
    <ActionConfirmDialog
      open={open}
      setOpen={setOpen}
      handleAccept={onAccept}
      handleCancel={onCancel}
      title="Confirmation"
      content={
        error?.replace("Please input a job size.", "Proceed?") ||
        "Are you sure you want to proceed?"
      }
    />
  );
}
